import cytoscape from 'cytoscape';

import {ShapeDrawing} from './lib/shape-drawing';
import {MapStateService} from '../app/api/services/map-state.service';
import {brightenWithHSL, darkenWithHSL} from '../app/shared/helpers/color-helpers';
import {cyNodeType} from '../app/shared/helpers/node-shape.helper';

const cy = cytoscape;

export function drawGradientNode(cy: any, mapStateService: any) {
  const layer = cy.cyCanvas();
  const canvas = layer.getCanvas();
  const ctx = canvas.getContext('2d');
  const drawing = new ShapeDrawing();
  cy.on('render cyCanvas.resize', () => {
    layer.resetTransform(ctx);
    layer.clear(ctx);
    layer.setTransform(ctx);

    // Query nodes with .collapsed CSS class
    // Also verify the node is actually collapsed by checking the expand-collapse plugin's internal state
    cy.$('.collapsed').not('.display-none').forEach(function (node: any) {
      // The expand-collapse plugin uses 'cy-expand-collapse-collapsed-node' class and stores
      // collapsed children in node.data('collapsedChildren'). If neither is present, the node
      // is actually expanded despite having .collapsed class (state mismatch after refresh)
      const hasPluginCollapsedClass = node.hasClass('cy-expand-collapse-collapsed-node');
      const hasCollapsedChildren = node.data('collapsedChildren') != null;
      if (!hasPluginCollapsedClass && !hasCollapsedChildren) {
        return;
      }
      if (node.parents().some((parent: any) => parent.hasClass('display-none'))) return;
      const nodeData = getNodeDataBasedOnState(node, mapStateService);
      const type = cyNodeType(nodeData.type);
      const editMode = localStorage.getItem('app-settings') ?
        JSON.parse(localStorage.getItem('app-settings') || '{}')?.isEditMode : false;
      if (!editMode && nodeData.excludedFromState) return;
      const bb = node.boundingBox({ includeLabels: false });
      const pos = { x: (bb.x1 + bb.x2) / 2, y: (bb.y1 + bb.y2) / 2 };
      const halfWidth = node.width() / 2;
      const halfHeight = node.height() / 2;
      const gradientCenterX = pos.x - halfWidth * 0.25;
      const gradientCenterY = pos.y - halfHeight * 0.25;
      const calculatedGradientRadius = calculateGradientRadiusBasedOnShape(type, halfWidth, halfHeight);
      const gradientRadiusX = calculatedGradientRadius.x;
      const gradientRadiusY = calculatedGradientRadius.y;
      const gradient = ctx.createRadialGradient(
        gradientCenterX, gradientCenterY, 0,
        pos.x, pos.y, gradientRadiusY
      );


      gradient.addColorStop(0, brightenWithHSL(nodeData.color, 0.8));
      gradient.addColorStop(1, darkenWithHSL(nodeData.color, 0.5));
      ctx.fillStyle = gradient;
      ctx.beginPath();

      if (type === 'ELLIPSE') {
        drawing.drawEllipsePath(ctx, pos.x, pos.y, 2 * gradientRadiusX, 2 * gradientRadiusY);
      } else if (type === 'RECTANGLE') {
        ctx.rect(pos.x - gradientRadiusX, pos.y - gradientRadiusY, 2 * gradientRadiusX, 2 * gradientRadiusY);
      } else if (type === 'TRIANGLE') {
        // Shift down slightly to align bottom edge with node shape
        drawPolygonFitToSquare(ctx, pos.x, pos.y + gradientRadiusY * 0.03, gradientRadiusX, gradientRadiusY, 3);
      } else if (type === 'DIAMOND' || type === 'ROUND-DIAMOND') {
        // Diamond has hardcoded points in Cytoscape: top, right, bottom, left
        ctx.moveTo(pos.x, pos.y - gradientRadiusY);           // top
        ctx.lineTo(pos.x + gradientRadiusX, pos.y);           // right
        ctx.lineTo(pos.x, pos.y + gradientRadiusY);           // bottom
        ctx.lineTo(pos.x - gradientRadiusX, pos.y);           // left
        ctx.closePath();
      } else if (type === 'ROUND-TRIANGLE') {
        const rad = gradientRadiusX / 4;
        const top = { x: pos.x, y: pos.y - gradientRadiusY };
        const bottomRight = { x: pos.x + gradientRadiusX, y: pos.y + gradientRadiusY };
        const bottomLeft = { x: pos.x - gradientRadiusX, y: pos.y + gradientRadiusY };
        ctx.moveTo(top.x, top.y + rad);
        ctx.lineTo(bottomLeft.x + rad, bottomLeft.y - rad);
        ctx.arcTo(bottomLeft.x, bottomLeft.y, pos.x, pos.y, rad);
        ctx.lineTo(bottomRight.x - rad, bottomRight.y - rad);
        ctx.arcTo(bottomRight.x, bottomRight.y, top.x, top.y, rad);
        ctx.lineTo(top.x, top.y + rad);
        ctx.arcTo(top.x, top.y, bottomLeft.x, bottomLeft.y, rad);
        ctx.closePath();
      } else if (type === 'ROUND-RECTANGLE') {
        const rad = gradientRadiusX / 4;
        ctx.moveTo(pos.x - gradientRadiusX + rad, pos.y - gradientRadiusY);
        ctx.arcTo(pos.x + gradientRadiusX, pos.y - gradientRadiusY, pos.x + gradientRadiusX, pos.y + gradientRadiusY, rad);
        ctx.arcTo(pos.x + gradientRadiusX, pos.y + gradientRadiusY, pos.x - gradientRadiusX, pos.y + gradientRadiusY, rad);
        ctx.arcTo(pos.x - gradientRadiusX, pos.y + gradientRadiusY, pos.x - gradientRadiusX, pos.y - gradientRadiusY, rad);
        ctx.arcTo(pos.x - gradientRadiusX, pos.y - gradientRadiusY, pos.x + gradientRadiusX, pos.y - gradientRadiusY, rad);
        ctx.closePath();
      } else if (type === 'BOTTOM-ROUND-RECTANGLE') {
        const rad = gradientRadiusX / 4;
        ctx.moveTo(pos.x - gradientRadiusX, pos.y - gradientRadiusY);
        ctx.lineTo(pos.x + gradientRadiusX, pos.y - gradientRadiusY);
        ctx.arcTo(pos.x + gradientRadiusX, pos.y + gradientRadiusY, pos.x - gradientRadiusX, pos.y + gradientRadiusY, rad);
        ctx.arcTo(pos.x - gradientRadiusX, pos.y + gradientRadiusY, pos.x - gradientRadiusX, pos.y - gradientRadiusY, rad);
        ctx.closePath();
      } else if (type === 'PENTAGON') {
        drawPolygonFitToSquare(ctx, pos.x, pos.y, gradientRadiusX, gradientRadiusY, 5);
      } else if (type === 'HEXAGON') {
        drawPolygonFitToSquare(ctx, pos.x, pos.y, gradientRadiusX, gradientRadiusY, 6);
      } else if (type === 'HEPTAGON') {
        drawPolygonFitToSquare(ctx, pos.x, pos.y, gradientRadiusX, gradientRadiusY, 7);
      } else if (type === 'OCTAGON') {
        drawPolygonFitToSquare(ctx, pos.x, pos.y, gradientRadiusX, gradientRadiusY, 8);
      } else if (type === 'RHOMBOID') {
        const skew = gradientRadiusX / 4;
        ctx.moveTo(pos.x - gradientRadiusX, pos.y - gradientRadiusY);
        ctx.lineTo(pos.x + gradientRadiusX - skew, pos.y - gradientRadiusY);
        ctx.lineTo(pos.x + gradientRadiusX, pos.y + gradientRadiusY);
        ctx.lineTo(pos.x - gradientRadiusX + skew, pos.y + gradientRadiusY);
        ctx.closePath();
      } else if (type === 'STAR') {
        const spikes = 5;
        const outerRadius = gradientRadiusX;
        const innerRadius = gradientRadiusX / 2.5;
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y - outerRadius);
        for (let i = 0; i < spikes; i++) {
          const angle = (i * 2 * Math.PI) / spikes;
          ctx.lineTo(pos.x + innerRadius * Math.sin(angle + Math.PI / spikes), pos.y - innerRadius * Math.cos(angle + Math.PI / spikes));
          ctx.lineTo(pos.x + outerRadius * Math.sin(angle + 2 * Math.PI / spikes), pos.y - outerRadius * Math.cos(angle + 2 * Math.PI / spikes));
        }
        ctx.closePath();
      } else if (type === 'TAG') {
        const cutSize = gradientRadiusX / 4;
        ctx.moveTo(pos.x - gradientRadiusX, pos.y - gradientRadiusY);
        ctx.lineTo(pos.x + gradientRadiusX - cutSize, pos.y - gradientRadiusY);
        ctx.lineTo(pos.x + gradientRadiusX, pos.y);
        ctx.lineTo(pos.x + gradientRadiusX - cutSize, pos.y + gradientRadiusY);
        ctx.lineTo(pos.x - gradientRadiusX, pos.y + gradientRadiusY);
        ctx.closePath();
      }
      if (nodeData.excludedFromState) {
        ctx.globalAlpha = 0.5;
      }
      ctx.fill();
      ctx.globalAlpha = 1.0;
      ctx.closePath();
    });
  });
}

// Generate polygon points matching Cytoscape's approach (fit to square bounding box)
// The scale parameter allows slight adjustment to account for visual border/stroke
function drawPolygonFitToSquare(ctx: any, centerX: number, centerY: number, halfWidth: number, halfHeight: number, sides: number, scale = 1.0) {
  // Generate unit ngon points (matching Cytoscape's generateUnitNgonPoints)
  const increment = (1.0 / sides) * 2 * Math.PI;
  // For odd-sided polygons, start at top (90 degrees). For even-sided, offset by half increment
  const startAngle = sides % 2 === 0 ? Math.PI / 2.0 + increment / 2.0 : Math.PI / 2.0;

  const points: number[] = [];
  for (let i = 0; i < sides; i++) {
    const currentAngle = i * increment + startAngle;
    points.push(Math.cos(currentAngle));      // x
    points.push(Math.sin(-currentAngle));     // y (flipped, matching Cytoscape)
  }

  // Fit to square (matching Cytoscape's fitPolygonToSquare)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < sides; i++) {
    minX = Math.min(minX, points[2 * i]);
    maxX = Math.max(maxX, points[2 * i]);
    minY = Math.min(minY, points[2 * i + 1]);
    maxY = Math.max(maxY, points[2 * i + 1]);
  }

  // Stretch to [-1, 1] range
  const sx = 2 / (maxX - minX);
  const sy = 2 / (maxY - minY);
  for (let i = 0; i < sides; i++) {
    points[2 * i] *= sx;
    points[2 * i + 1] *= sy;
  }

  // Recalculate bounds and shift to center at origin
  minX = Infinity;
  minY = Infinity;
  maxX = -Infinity;
  maxY = -Infinity;
  for (let i = 0; i < sides; i++) {
    minX = Math.min(minX, points[2 * i]);
    maxX = Math.max(maxX, points[2 * i]);
    minY = Math.min(minY, points[2 * i + 1]);
    maxY = Math.max(maxY, points[2 * i + 1]);
  }
  for (let i = 0; i < sides; i++) {
    points[2 * i] += (-1 - minX);
    points[2 * i + 1] += (-1 - minY);
  }

  // Draw the polygon scaled to the node's dimensions
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const x = centerX + points[2 * i] * halfWidth * scale;
    const y = centerY + points[2 * i + 1] * halfHeight * scale;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function calculateGradientRadiusBasedOnShape(shape: string, width: number, height: number) {
  // Polygons now use drawPolygonFitToSquare which properly fits to the bounding box,
  // so no multiplier adjustments are needed
  return {x: width, y: height};
}

function getNodeDataBasedOnState(node: any, mapStateService: MapStateService) {
  const currentMapState = mapStateService.getCurrentLocalState();
  const isDefaultState = currentMapState?.id === undefined;
  let nodeData = node.data();
  if (!isDefaultState) {
    const statedNode = nodeData.states?.find((s: any) => s.stateId === currentMapState?.id);
    if (statedNode) nodeData = statedNode.node;
  }
  return nodeData;
}
